import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Dialog,
  DialogProps,
  IconButton,
  IconButtonProps,
  Tabs,
  TabsProps,
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { DataGrid, DataGridProps } from '@mui/x-data-grid';

const mapSizes = {
  small: {
    '.MuiPaper-root': {
      maxWidth: '424px',
      width: '100%',
    },
  },
  medium: {
    '.MuiPaper-root': {
      maxWidth: '540px',
      width: '100%',
    },
  },
  xl: {
    '.MuiPaper-root': {
      maxWidth: '1080px',
      width: '100%',
    },
  },
};

export const StyledTabs = styled(Tabs)<TabsProps>(() => ({
  width: '100%',
}));

export const StyledAreaBox = styled(Box)<BoxProps & { theme?: Theme }>(() => ({
  borderRadius: 'var(--sk-radius-lg)',
  border: '1px solid var(--sk-surface-border)',
  backgroundColor: 'var(--sk-surface-default)',
}));

export const StyledShadowLessButton = styled(Button)<ButtonProps>({
  boxShadow: 'none',
});

export const StyledModal = styled(Dialog)<DialogProps & { size?: 'small' | 'medium' | 'xl' }>(({ size }) => ({
  ...(size && mapSizes[size]),
  '& .MuiDialog-paper': {
    backgroundColor: 'var(--sk-bg-secondary)',
    border: '1px solid var(--sk-surface-border)',
    borderRadius: 'var(--sk-radius-lg)',
  },
}));

export const StyledCancelIconButton = styled(IconButton)<IconButtonProps>({
  padding: '0',
  color: 'var(--sk-text-secondary)',
  '&:hover': {
    backgroundColor: 'var(--sk-surface-hover)',
    color: 'var(--sk-text-primary)',
  },
});

/**
 * StyledDataGrid for Settings with theme-v2 CSS custom properties
 * Uses card-like row design with rounded borders
 */
export const StyledDataGrid = styled(DataGrid)<DataGridProps & { theme?: Theme }>(() => ({
  border: 'none',
  backgroundColor: 'transparent',
  fontFamily: 'var(--sk-font-body)',
  color: 'var(--sk-text-primary)',

  '& .MuiDataGrid-cell': {
    padding: '10px 0',
    borderBottom: 'none',
    color: 'var(--sk-text-primary)',
    '&:focus, &:focus-within': {
      outline: 'none',
    },
  },

  '&.MuiDataGrid-root': {
    '--DataGrid-rowBorderColor': 'transparent',
    height: '100%',
  },

  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'transparent',
    borderBottom: 'none',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: 'transparent',
    '&:focus, &:focus-within': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontFamily: 'var(--sk-font-display)',
    fontWeight: 600,
    fontSize: 'var(--sk-text-xs)',
    color: 'var(--sk-text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },

  '& .MuiDataGrid-footerContainer': {
    border: 'transparent',
    backgroundColor: 'transparent',
  },
  '& .MuiTablePagination-root': {
    color: 'var(--sk-text-secondary)',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    color: 'var(--sk-text-secondary)',
    fontSize: 'var(--sk-text-sm)',
  },
  '& .MuiTablePagination-actions .MuiIconButton-root': {
    color: 'var(--sk-text-secondary)',
    '&:hover': {
      backgroundColor: 'var(--sk-surface-hover)',
    },
    '&.Mui-disabled': {
      color: 'var(--sk-text-tertiary)',
    },
  },

  // Card-like row styling with border
  '& .MuiDataGrid-row': {
    position: 'relative',
    borderRadius: 'var(--sk-radius-lg)',
    backgroundColor: 'var(--sk-surface-default)',
    marginBottom: 'var(--sk-space-2)',
    transition: 'all 0.2s ease',
    '&::after': {
      content: "''",
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: 'block',
      border: '1px solid var(--sk-surface-border)',
      pointerEvents: 'none',
      borderRadius: 'var(--sk-radius-lg)',
      zIndex: 1,
      transition: 'border-color 0.2s ease',
    },
    '&:hover': {
      backgroundColor: 'var(--sk-surface-hover)',
      '&::after': {
        borderColor: 'var(--sk-surface-border-hover)',
      },
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(0, 200, 224, 0.08)',
      '&::after': {
        borderColor: 'var(--sk-cyan-700)',
      },
    },
  },

  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': { display: 'none' },

  '& .MuiDataGrid-cell--withRenderer': {
    border: 'transparent',
  },

  '&.MuiDataGrid-root .MuiDataGrid-withBorderColor': {
    border: 'transparent',
  },

  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },

  // Checkbox styling
  '& .MuiCheckbox-root': {
    color: 'var(--sk-text-tertiary)',
    '&.Mui-checked': {
      color: 'var(--sk-cyan-500)',
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 200, 224, 0.1)',
    },
  },
}));
